// Friendly fallbacks for API errors, keyed by the `code` the server sends in its
// error envelope (see server/utils/httpErrors.js).
//
// These are FALLBACKS. When the API sends its own message we show that, because
// it can say something specific about what went wrong. These cover the cases it
// cannot: the request never reached the server, or it came back without usable
// copy. Keeping them here means the ~70 call sites that render API errors do not
// each need their own wording.
//
// Voice: say what happened and what to do next. Use contractions. Never name a
// status code, a field, or an account role.

export const GENERIC_ERROR_MESSAGE =
  "Something went wrong on our end. Nothing was lost — try again in a moment.";

export const OFFLINE_ERROR_MESSAGE =
  "We couldn't reach Haastia. Check your connection and try again.";

export const ERROR_MESSAGES_BY_CODE = Object.freeze({
  VALIDATION_ERROR: "Something there didn't look right. Check your details and try again.",
  AUTH_REQUIRED: "You'll need to sign in again to keep going.",
  FORBIDDEN: "You don't have access to this. Nothing was changed.",
  NOT_FOUND: "We couldn't find that. It may have been moved or removed.",
  CONFLICT:
    "Someone else changed this while you were working. Refresh to see the latest, then try again.",
  PAYLOAD_TOO_LARGE: "That file is too large. Try a smaller one.",
  UNSUPPORTED_MEDIA_TYPE: "We can't use that kind of file. Try a different format.",
  UNPROCESSABLE_ENTITY: "We couldn't process that. Check your details and try again.",
  RATE_LIMITED: "That's a lot of tries in a short time. Wait a moment, then try again.",
  INTERNAL_ERROR: GENERIC_ERROR_MESSAGE,
});

// Prefer the server's own message; fall back to something human otherwise.
export const resolveErrorMessage = ({ message, code, hasResponse = true } = {}) => {
  const trimmed = typeof message === "string" ? message.trim() : "";
  if (trimmed) return trimmed;
  if (code && ERROR_MESSAGES_BY_CODE[code]) return ERROR_MESSAGES_BY_CODE[code];
  return hasResponse ? GENERIC_ERROR_MESSAGE : OFFLINE_ERROR_MESSAGE;
};
